import { Component, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { IToolPanelAngularComp } from 'ag-grid-angular';
import { GridApi, IToolPanelParams } from 'ag-grid-community';
import { SettingPanelService } from './setting-panel.service';
import { Subscription } from 'rxjs';
import { TDGridSettingsService } from '../tdgrid-settings.service';

@Component({
    selector: 'app-setting-panel',
    templateUrl: './setting-panel.component.html',
    styleUrls: ['./setting-panel.component.scss'],
})
export class SettingPanelComponent implements IToolPanelAngularComp, OnDestroy {
    private gridApi!: GridApi;
    private subscription: Subscription = new Subscription();

    // Column management
    allColumns: any[] = [];
    filteredColumns: any[] = [];
    searchTerm: string = '';

    // Drag and drop
    draggedColumn: any = null;

    isSaveLoading: boolean = false;
    isResetLoading: boolean = false;

    constructor(
        private settingsService: SettingPanelService,
        private cdr: ChangeDetectorRef,
        private gridSettingsService: TDGridSettingsService,
    ) {}

    agInit(params: IToolPanelParams): void {
        this.gridApi = params.api;

        // Subscribe to grid events to refresh column state
        this.setupGridEventListeners();
        this.subscription.add(
            this.settingsService.refresh$.subscribe(() => {
                this.refreshColumnState();
            }),
        );

        this.subscription.add(
            this.settingsService.saveLoading$.subscribe((loading) => {
                this.isSaveLoading = loading;
                this.cdr.detectChanges();
            }),
        );

        this.subscription.add(
            this.settingsService.resetLoading$.subscribe((loading) => {
                this.isResetLoading = loading;
                this.cdr.detectChanges();
            }),
        );

        // Initialize with a delay to ensure grid is ready
        setTimeout(() => {
            this.refreshColumnState();
        }, 100);
    }

    private setupGridEventListeners(): void {
        // Listen for column visibility changes
        this.gridApi.addEventListener('columnVisibleChanged', () => {
            setTimeout(() => this.refreshColumnState(), 50);
        });

        // Listen for column moves
        this.gridApi.addEventListener('columnMoved', () => {
            this.refreshColumnState();
        });
        // Listen for column pinned changes
        this.gridApi.addEventListener('columnPinnedChanged', () => {
            this.refreshColumnState();
        });
        // Listen for model updates
        this.gridApi.addEventListener('modelUpdated', () => {
            this.refreshColumnState();
        });

        // Listen for row data updates
        this.gridApi.addEventListener('rowDataUpdated', () => {
            this.refreshColumnState();
        });
    }
    refreshColumnState(): void {
        if (this.gridApi && this.gridApi.getAllGridColumns) {
            this.allColumns = this.gridApi
                .getAllGridColumns()
                .filter((col) => col.getColDef().headerName) // Only show columns with headers
                .map((col) => ({
                    id: col.getColId(),
                    name: col.getColDef().headerName || col.getColId(),
                    visible: col.isVisible(),
                    width: col.getActualWidth(),
                    pinned: col.getPinned() === 'left',
                }));

            this.filterColumns();

            // Force change detection to update the UI
            this.cdr.detectChanges();
        }
    }

    onToggleColumnVisibility(column: any, index: number): void {
        if (this.gridApi) {
            const columnState = this.gridApi.getColumnState();
            const colIndex = columnState.findIndex((col) => col.colId === column.id);

            if (colIndex !== -1) {
                // Toggle the hide property based on the checkbox state
                columnState[colIndex].hide = !columnState[colIndex].hide;

                // Use centralized method to apply column state
                this.gridSettingsService.applyColumnState(this.gridApi, columnState, { applyOrder: true }).subscribe({
                    next: (success) => {
                        if (success) {
                            this.filteredColumns[index].visible = !columnState[colIndex].hide;
                        }
                    },
                });
            }
        }
    }

    onToggleColumnPin(column: any, index: number): void {
        if (this.gridApi) {
            const columnState = this.gridApi.getColumnState();
            const colIndex = columnState.findIndex((col) => col.colId === column.id);

            if (colIndex !== -1) {
                // Toggle the pinned property
                const newPinnedValue = columnState[colIndex].pinned === 'left' ? null : 'left';
                columnState[colIndex].pinned = newPinnedValue;

                // Use centralized method to apply column state
                this.gridSettingsService.applyColumnState(this.gridApi, columnState, { applyOrder: true }).subscribe({
                    next: (success) => {
                        if (success) {
                            // Update the local column data
                            this.filteredColumns[index].pinned = newPinnedValue === 'left';
                        }
                    },
                });
            }
        }
    }

    onSearchChange(): void {
        this.filterColumns();
    }

    filterColumns(): void {
        if (!this.searchTerm.trim()) {
            this.filteredColumns = [...this.allColumns];
        } else {
            this.filteredColumns = this.allColumns.filter((col) => col.name.toLowerCase().includes(this.searchTerm.toLowerCase()));
        }
        this.cdr.detectChanges();
    }

    onSaveClick(): void {
        // Emit save action to td-grid component
        this.settingsService.saveSettings();
    }

    onResetClick(): void {
        // Emit reset action to td-grid component
        this.settingsService.resetSettings();
    }

    refresh(): void {
        this.refreshColumnState();
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}
